const EXCLUDED_KEYS = ['return_to'];

/**
 * Parses a URL query string into a query params object.
 * It converts comma-separated values into arrays, and 'true' or 'false'
 * strings into boolean values. If the input is not a string, it returns
 * an empty object.
 *
 * @param {string} urlQueryString - Query string from URL, ie '?foo=bar&baz=qux'
 *                                  Usually obtained via window.location.search
 * @returns {Object} An object containing the parsed query parameters
 */
export function extractQueryParams(urlQueryString) {
  if (typeof urlQueryString !== 'string') {
    return {};
  }
  const rawParams = new URLSearchParams(urlQueryString);
  const formattedParams = {};
  for (const [key, value] of rawParams.entries()) {
    if (value.includes(',')) {
      formattedParams[key] = value.split(',');
    } else if (value === 'true') {
      formattedParams[key] = true;
    } else if (value === 'false') {
      formattedParams[key] = false;
    } else {
      formattedParams[key] = value;
    }
  }
  return formattedParams;
}

/**
 * Takes a query params object and encodes it as a query string.
 *
 * @param {Object} params - An object containing the query params to encode
 * @returns {string} The encoded query string, ie. `foo=bar&baz=qux`
 */

export function encodeQueryParams(params) {
  if (!params) {
    return '';
  }
  const formattedParams = {};
  for (const key in params) {
    const value = params[key];
    if (value === null || value === undefined) {
      delete formattedParams[key];
    } else if (typeof value === 'boolean') {
      formattedParams[key] = `${value}`;
    } else if (Array.isArray(value)) {
      formattedParams[key] = value.join(',');
    } else {
      formattedParams[key] = value;
    }
  }
  return new URLSearchParams(formattedParams).toString();
}

/**
 * processQueryStringForCache returns a query string that can be used for
 * caching purposes. It removes protected and excluded keys.
 * This function is useful for omitting navigation type query params from
 * the cache, so that they are not used as a fallback in future navigations.
 * @param {string} fullQueryString - ie. `?foo=bar&baz=qux`
 * @param {Array} excludeKeys - ie. ['foo']
 * @returns {string} The processed query string, ie. `baz=qux`
 */

function processQueryStringForCache(fullQueryString, excludeKeys = []) {
  const queryParamsForCaching = new URLSearchParams(fullQueryString);
  EXCLUDED_KEYS.concat(excludeKeys).forEach((key) =>
    queryParamsForCaching.delete(key),
  );
  const queryStringForCache = queryParamsForCaching.toString();

  return queryStringForCache;
}

/**
 * getAndSyncQueryParams returns the set of query params to use, based on
 * the URL path and cached value in the store, if present. It prioritizes the
 * URL. Once the query params have been determined, this function ensures that
 * the URL and cache are in sync.
 *
 * @param {string} fullPath - ie. `/foo/bar?baz=qux`
 * @param {Object} router - The Vue router instance to use for navigation
 * @param {Object} store - The Vuex store to read and write from the cache
 * @returns {Object} The compiled query params object for the given path
 */

export function getAndSyncQueryParams({
  fullPath,
  router,
  store,
  excludeKeys = [],
}) {
  if (typeof fullPath !== 'string') {
    throw new Error('Must provide a URL');
  }
  if (!router) {
    throw new Error('Must provide a router');
  }
  if (!store) {
    throw new Error('Must provide a store');
  }
  if (!fullPath.startsWith('/')) {
    throw new Error('Must provide a path starting with "/"');
  }

  const [pathname, queryStringFromURL = ''] = fullPath.split('?');
  const queryStringFromCache =
    store.getters['queryStrings/getCachedQueryStringForPath'](pathname);

  // Prioritize the query string in URL over cached value
  const queryStringToUse = queryStringFromURL || queryStringFromCache || '';
  const queryStringForCache = processQueryStringForCache(
    queryStringToUse,
    excludeKeys,
  );

  // Sync the filtered query string to the cache
  if (queryStringForCache !== queryStringFromCache) {
    store.commit('queryStrings/cacheQueryStringForPath', {
      pathname,
      queryString: queryStringForCache,
    });
  }

  // If the queryStringToUse is different from what's currently in the URL, update the URL
  if (queryStringToUse !== queryStringFromURL) {
    router.replace(`${pathname}?${queryStringToUse}`);
  }

  return extractQueryParams(queryStringToUse);
}

/**
 * updateQueryParams updates the query params for the current path with the
 * provided updates.
 *
 * @param {string} fullPath - ie. `/foo/bar?baz=qux`
 * @param {Object} router - The Vue router instance to use for navigation
 * @param {Object} updates - The updates to apply to the current query params
 * @returns {void}
 */

export function updateQueryParams({ fullPath, router, store, updates }) {
  if (
    typeof fullPath !== 'string' ||
    !router ||
    !store ||
    !updates ||
    typeof updates !== 'object'
  ) {
    throw new Error('Must provide a fullPath, router, and updates object');
  }
  const [pathname, queryString] = fullPath.split('?');
  const params = extractQueryParams(queryString);
  const updatedParams = {
    ...params,
    ...updates,
  };

  // if params haven't changed, do nothing
  if (JSON.stringify(params) === JSON.stringify(updatedParams)) {
    return;
  }

  const encoded = encodeQueryParams(updatedParams);

  // if query params were empty and they are still empty, do nothing
  if (!queryString && !encoded) {
    return;
  }

  // if we're intentionally updating to empty query params, update the cached
  // value in store so it's not used as a fallback in future navigations
  if (!encoded) {
    store.commit('queryStrings/cacheQueryStringForPath', {
      pathname,
      queryString: '',
    });
  }

  return router.push(`${pathname}?${encoded}`).catch(() => {});
}

/**
 * deleteAllQueryParams removes all query params from the current path.
 * This function is useful for resetting the query params to their defaults.
 * It also updates the cache in the store to reflect the removal of all query
 * params.
 * @param {string} fullPath - ie. `/foo/bar?baz=qux`
 * @param {Object} router - The Vue router instance to use for navigation
 * @param {Object} store - The Vuex store to read and write from the cache
 * @returns {void}
 */

export function deleteAllQueryParams({ fullPath, router, store }) {
  if (typeof fullPath !== 'string' || !router || !store) {
    throw new Error('Must provide a fullPath, router, and store');
  }

  if (!fullPath.startsWith('/')) {
    throw new Error('Must provide a path starting with "/"');
  }

  const [pathname, queryString] = fullPath.split('?');
  if (!queryString) {
    return;
  }

  store.commit('queryStrings/cacheQueryStringForPath', {
    pathname,
    queryString: '',
  });

  return router.push(pathname);
}

/**
 * navigateWithQueryParams navigates to the given pathname with the provided
 * query params.
 *
 * @param {Object} router - The Vue router instance to use for navigation
 * @param {string} pathname - The pathname to navigate to, ie. /analytics
 * @param {Object} params - The query params to use, ie. { is_fraudulent: true }
 * @returns {void}
 */

export function navigateWithQueryParams({ router, pathname, params = {} }) {
  if (!router || typeof pathname !== 'string' || typeof params !== 'object') {
    throw new Error('Must provide a router and pathname');
  }
  const encoded = encodeQueryParams(params);
  return router.push(`${pathname}?${encoded}`);
}
